import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ContentGateModal, gateFor } from '@/components/ContentGate';
import { useAuth } from '@/contexts/AuthContext';
import { BackButton } from '@/components/BackButton';
import SEO from '@/components/SEO';
import { BreadcrumbSchema, CourseSchema, FAQSchema } from '@/components/StructuredData';
import RelatedPages from '@/components/RelatedPages';
import VisibleBreadcrumbs from '@/components/VisibleBreadcrumbs';
import { 
  BookOpen, 
  Clock, 
  ArrowRight,
  Loader2,
  CheckCircle,
  Lock, UserPlus,
  RefreshCw,
  Flag
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API } from '@/config';
import { getPrefetched, getResolvedData, prefetchDetail } from '@/utils/dataPrefetcher';

// Translation helper for all 9 languages
const texts = {
  subscriptionRequired: { en: 'Subscription required', ar: 'يجب الاشتراك للوصول للدروس', sv: 'Prenumeration krävs', tr: 'Abonelik gerekli', fa: 'اشتراک لازم است', so: 'Qormada loo baahan yahay', pl: 'Wymagana subskrypcja', es: 'Suscripción requerida', de: 'Abonnement erforderlich' },
  scrollDown: { en: 'Scroll down for more', ar: 'مرر للأسفل لمشاهدة المزيد', sv: 'Scrolla ner för mer', tr: 'Daha fazlası için aşağı kaydırın', fa: 'برای بیشتر به پایین بروید', so: 'Hoos u dhaadhac si aad u aragto wax badan', pl: 'Przewiń w dół po więcej', es: 'Desplázate hacia abajo para más', de: 'Nach unten scrollen für mehr' },
  min: { en: 'min', ar: 'دقيقة', sv: 'min', tr: 'dk', fa: 'دقیقه', so: 'daqiiqo', pl: 'min', es: 'min', de: 'min' },
  total: { en: 'Total', ar: 'إجمالي', sv: 'Totalt', tr: 'Toplam', fa: 'مجموع', so: 'Wadarta', pl: 'Łącznie', es: 'Total', de: 'Gesamt' },
  lessons: { en: 'lessons', ar: 'درس', sv: 'lektioner', tr: 'ders', fa: 'درس', so: 'casharo', pl: 'lekcji', es: 'lecciones', de: 'Lektionen' },
  subscribersOnly: { en: 'Subscribers Only Content', ar: 'محتوى للمشتركين فقط', sv: 'Endast för prenumeranter', tr: 'Sadece Aboneler İçin', fa: 'فقط برای مشترکین', so: 'Waxyaabaha qoraga kaliya', pl: 'Treść tylko dla subskrybentów', es: 'Contenido solo para suscriptores', de: 'Nur für Abonnenten' },
  subscribeDesc: { en: 'Subscribe now to access all lessons and exclusive content!', ar: 'اشترك الآن للوصول إلى جميع الدروس والمحتوى الحصري!', sv: 'Prenumerera nu för att få tillgång till alla lektioner och exklusivt innehåll!', tr: 'Tüm derslere ve özel içeriğe erişmek için şimdi abone olun!', fa: 'برای دسترسی به همه دروس و محتوای انحصاری اشتراک بگیرید!', so: 'Hadda iska qor si aad u hesho dhammaan casharada iyo waxyaabaha gaarka ah!', pl: 'Subskrybuj, aby uzyskać dostęp do wszystkich lekcji!', es: '¡Suscríbete ahora para acceder a todas las lecciones!', de: 'Abonnieren Sie für alle Lektionen!' },
  subscribeNow: { en: 'Subscribe Now 🚀', ar: 'اشترك الآن 🚀', sv: 'Prenumerera nu 🚀', tr: 'Şimdi Abone Ol 🚀', fa: 'اشتراک 🚀', so: 'Hadda Iska Qor 🚀', pl: 'Subskrybuj 🚀', es: 'Suscríbete 🚀', de: 'Jetzt Abonnieren 🚀' },
  cancel: { en: 'Cancel', ar: 'إلغاء', sv: 'Avbryt', tr: 'İptal', fa: 'لغو', so: 'Jooji', pl: 'Anuluj', es: 'Cancelar', de: 'Abbrechen' },
  reportError: { en: 'Report Error', ar: 'الإبلاغ عن خطأ', sv: 'Rapportera fel', tr: 'Hata Bildir', fa: 'گزارش خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar Error', de: 'Fehler melden' },
  reportTitle: { en: 'Report an Error', ar: 'الإبلاغ عن خطأ', sv: 'Rapportera ett fel', tr: 'Bir Hata Bildir', fa: 'گزارش یک خطا', so: 'Ka warbixin khalad', pl: 'Zgłoś błąd', es: 'Reportar un error', de: 'Einen Fehler melden' },
  reportType: { en: 'Report Type', ar: 'نوع البلاغ', sv: 'Typ av rapport', tr: 'Rapor Türü', fa: 'نوع گزارش', so: 'Nooca warbixinta', pl: 'Typ raportu', es: 'Tipo de reporte', de: 'Berichttyp' },
  incorrectInfo: { en: 'Incorrect Information', ar: 'معلومات غير صحيحة', sv: 'Felaktig information', tr: 'Yanlış Bilgi', fa: 'اطلاعات نادرست', so: 'Macluumaad khaldan', pl: 'Nieprawidłowe informacje', es: 'Información incorrecta', de: 'Falsche Information' },
  imageIssue: { en: 'Image Issue', ar: 'مشكلة في الصورة', sv: 'Bildproblem', tr: 'Resim Sorunu', fa: 'مشکل تصویر', so: 'Dhibaato sawir', pl: 'Problem z obrazem', es: 'Problema de imagen', de: 'Bildproblem' },
  translationError: { en: 'Translation Error', ar: 'خطأ في الترجمة', sv: 'Översättningsfel', tr: 'Çeviri Hatası', fa: 'خطای ترجمه', so: 'Khalad turjumaad', pl: 'Błąd tłumaczenia', es: 'Error de traducción', de: 'Übersetzungsfehler' },
  otherIssue: { en: 'Other Issue', ar: 'مشكلة أخرى', sv: 'Annat problem', tr: 'Diğer Sorun', fa: 'مشکل دیگر', so: 'Dhibaato kale', pl: 'Inny problem', es: 'Otro problema', de: 'Anderes Problem' },
  details: { en: 'Details', ar: 'التفاصيل', sv: 'Detaljer', tr: 'Detaylar', fa: 'جزئیات', so: 'Faahfaahin', pl: 'Szczegóły', es: 'Detalles', de: 'Details' },
  describeIssue: { en: 'Please describe the issue...', ar: 'يرجى وصف المشكلة...', sv: 'Beskriv problemet...', tr: 'Lütfen sorunu açıklayın...', fa: 'لطفاً مشکل را شرح دهید...', so: 'Fadlan sharax dhibaatada...', pl: 'Proszę opisać problem...', es: 'Por favor describa el problema...', de: 'Bitte beschreiben Sie das Problem...' },
  submit: { en: 'Submit', ar: 'إرسال', sv: 'Skicka', tr: 'Gönder', fa: 'ارسال', so: 'Dir', pl: 'Wyślij', es: 'Enviar', de: 'Absenden' },
  reportSuccess: { en: 'Report submitted successfully!', ar: 'تم إرسال البلاغ بنجاح!', sv: 'Rapporten har skickats!', tr: 'Rapor başarıyla gönderildi!', fa: 'گزارش با موفقیت ارسال شد!', so: 'Warbixinta si guul leh ayaa loo diray!', pl: 'Raport wysłany pomyślnie!', es: '¡Reporte enviado con éxito!', de: 'Bericht erfolgreich gesendet!' },
  reportFailed: { en: 'Failed to submit report', ar: 'فشل في إرسال البلاغ', sv: 'Det gick inte att skicka rapporten', tr: 'Rapor gönderilemedi', fa: 'ارسال گزارش ناموفق بود', so: 'Waa lagu guul daraystay dirista warbixinta', pl: 'Nie udało się wysłać raportu', es: 'Error al enviar el reporte', de: 'Bericht konnte nicht gesendet werden' },
};

const getText = (key, lang) => texts[key]?.[lang] || texts[key]?.en || key;

const LessonsPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [lessons, setLessons] = useState(() => getResolvedData('lessons') || []);
  const [loading, setLoading] = useState(() => !getResolvedData('lessons'));
  const [refreshing, setRefreshing] = useState(false);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportingLesson, setReportingLesson] = useState(null);
  const [reportType, setReportType] = useState('incorrect_info');
  const [reportDetails, setReportDetails] = useState('');
  const [submittingReport, setSubmittingReport] = useState(false);
  const lang = i18n.language;
  const isRTL = lang === 'ar' || lang === 'fa';
  
  // التحقق إذا كان المستخدم مشترك
  const isSubscribed = user?.subscription_status === 'active' || user?.taxi_subscription_status === 'active' || user?.is_admin || user?.role === 'super_admin';

  const fetchLessons = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) setRefreshing(true);
      // Try prefetched data first
      const prefetched = !isRefresh ? await getPrefetched('lessons') : null;
      if (prefetched) {
        setLessons(prefetched);
      } else {
        const response = await axios.get(`${API}/lessons?t=${Date.now()}`);
        if (response.data) {
          setLessons(response.data);
        }
      }
    } catch (error) {
      console.error('Error fetching lessons:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  // تحميل تفاصيل أول درسين مجانيين تلقائياً
  useEffect(() => {
    if (lessons.length > 0) {
      lessons.slice(0, 2).forEach(lesson => {
        prefetchDetail('lesson', lesson.slug || lesson.id);
      });
    }
  }, [lessons]);

  // Generate slug from Swedish title
  const generateSlug = (title) => {
    if (!title) return '';
    const sv = typeof title === 'string' ? title : (title.sv || title.en || '');
    return sv.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim()
      .replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
  };

  const getLessonPath = (lesson) => {
    const slug = generateSlug(lesson.title);
    const langPaths = {
      sv: '/teorilektioner', ar: '/ar/doross', en: '/en/lessons', tr: '/tr/dersler',
      fa: '/fa/dars-ha', so: '/so/casharada', de: '/de/lektionen', es: '/es/lecciones', pl: '/pl/lekcje',
    };
    return `${langPaths[i18n.language] || langPaths.sv}/${slug || lesson.id}`;
  };

  // دالة للتعامل مع النقر على الدرس
  const handleLessonClick = (lesson, gate = 'plans') => {
    if (isSubscribed || gate === 'open') {
      navigate(getLessonPath(lesson), { state: { lessonData: lesson } });
    } else {
      setShowSubscribeModal(gate);
    }
  };

  // دالة للإبلاغ عن خطأ
  const handleReportClick = (e, lesson) => {
    e.stopPropagation();
    setReportingLesson(lesson);
    setReportType('incorrect_info');
    setReportDetails('');
    setShowReportModal(true);
  };

  // دالة لإرسال البلاغ
  const handleSubmitReport = async () => {
    if (!reportDetails.trim()) return;
    
    setSubmittingReport(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/site-reports`,
        {
          url: `/teorilektioner`,
          report_type: reportType,
          details: `[Lesson: ${getLessonTitle(reportingLesson)}] ${reportDetails}`,
          item_id: reportingLesson?.id,
          item_type: 'lesson'
        },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {}
      );
      toast.success(getText('reportSuccess', lang));
      setShowReportModal(false);
      setReportingLesson(null);
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(getText('reportFailed', lang));
    } finally {
      setSubmittingReport(false);
    }
  };

  const getLessonTitle = (lesson) => {
    if (lesson.title && typeof lesson.title === 'object') {
      return lesson.title[lang] || lesson.title['en'] || lesson.title['sv'] || 'Untitled';
    }
    return lesson.title || 'Untitled';
  };

  const getLessonDescription = (lesson) => {
    if (lesson.description && typeof lesson.description === 'object') {
      return lesson.description[lang] || lesson.description['en'] || lesson.description['sv'] || '';
    }
    return lesson.description || '';
  };

  const getLessonCategory = (lesson) => {
    const category = lesson.category || 'general';
    return t(`lessons_category_${category}`);
  };

  const categoryColors = {
    traffic_rules: 'from-blue-600 to-cyan-600',
    traffic_signs: 'from-purple-600 to-pink-600',
    road_rules: 'from-blue-600 to-indigo-600',
    safety: 'from-green-600 to-emerald-600',
    parking: 'from-orange-600 to-red-600',
    theory: 'from-indigo-600 to-violet-600',
    general: 'from-slate-600 to-slate-700'
  };

  if (loading && lessons.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-950" dir={isRTL ? 'rtl' : 'ltr'}>
      <SEO page="lessons" />
      <CourseSchema language={i18n.language} />
      <FAQSchema language={i18n.language} page="home" />
      <BreadcrumbSchema items={[
        { name: i18n.language === 'ar' ? 'الرئيسية' : 'Home', url: 'https://drivemaster.se' },
        { name: i18n.language === 'ar' ? 'الدروس' : i18n.language === 'sv' ? 'Teorilektioner' : 'Lessons', url: 'https://drivemaster.se/teorilektioner' }
      ]} />
      <Navbar />
      <VisibleBreadcrumbs />
      <main className="flex-grow container mx-auto px-3 sm:px-4 pt-3 pb-4 sm:pb-8">
        <BackButton to="/" className="mb-2 sm:mb-4" />
        
        {/* Compact Header */}
        <div className="text-center mb-6 sm:mb-10">
          <div className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-slate-900 dark:text-slate-100">
              {t('lessons_title')} 📚
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => fetchLessons(true)}
              disabled={refreshing}
              className="rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 h-8 w-8 sm:h-10 sm:w-10"
              title={i18n.language === 'ar' ? 'تحديث' : i18n.language === 'sv' ? 'Uppdatera' : 'Refresh'}
            >
              <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 text-slate-500 dark:text-slate-400 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <p className="text-sm sm:text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto px-2 hidden sm:block">
            {t('lessons_subtitle')}
          </p>
          {!isSubscribed && (
            <div className="mt-3 sm:mt-5 inline-flex items-center gap-2 sm:gap-2.5 bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 px-4 sm:px-5 py-2 sm:py-2.5 rounded-full text-sm sm:text-base font-medium border border-transparent dark:border-amber-800/30">
              <Lock className="w-4 h-4 sm:w-5 sm:h-5" />
              {getText('subscriptionRequired', lang)}
            </div>
          )}
        </div>

        {lessons.length === 0 ? (
          <Card className="p-12 text-center">
            <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">{t('lessons_no_lessons')}</p>
          </Card>
        ) : (
          <>
            {/* Scroll indicator for mobile */}
            <div className="flex items-center justify-center gap-2 mb-5 text-slate-500 text-sm sm:text-base font-medium animate-bounce sm:hidden">
              <span>👇</span>
              <span>{getText('scrollDown', lang)}</span>
              <span>👇</span>
            </div>
            
            {/* Compact Grid for mobile, larger for desktop */}
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4">
              {lessons.map((lesson, index) => {
                const category = lesson.category || 'general';
                const isCompleted = lesson.completed || false;
                // أول بطاقتين مجانيتين للزوار والمستخدمين المجانيين
                const gate = gateFor(index, user, isSubscribed);
                const showLock = gate !== 'open';
                
                return (
                  <div
                    key={lesson.id || index}
                    className="group bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl border-2 border-slate-100 dark:border-slate-700 hover:border-emerald-400 dark:hover:border-emerald-600 hover:shadow-lg transition-all duration-200 cursor-pointer overflow-hidden relative"
                    onClick={() => handleLessonClick(lesson, gate)}
                    data-testid={`lesson-card-${index}`}
                    onMouseEnter={() => {
                      const slug = generateSlug(lesson.title);
                      prefetchDetail('lesson', slug || lesson.id);
                    }}
                  >
                    {/* Lock overlay for non-subscribers (except first 2) */}
                    {showLock && (
                      <div className="absolute inset-0 bg-slate-900/40 dark:bg-black/60 backdrop-blur-[1px] z-10 flex items-center justify-center cursor-pointer" onClick={(e) => { e.stopPropagation(); setShowSubscribeModal(gate); }} data-testid="content-lock-overlay">
                        <div className="bg-white/90 dark:bg-slate-700/90 rounded-full p-2">
                          {gate === 'register' ? <UserPlus className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600 dark:text-blue-300" /> : <Lock className="w-4 h-4 sm:w-5 sm:h-5 text-slate-700 dark:text-slate-300" />}
                        </div>
                      </div>
                    )}
                    
                    {/* Report Button */}
                    <button
                      onClick={(e) => handleReportClick(e, lesson)}
                      className={`absolute top-1 ${isRTL ? 'right-1' : 'left-1'} z-20 p-1.5 sm:p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-md`}
                      title={getText('reportError', lang)}
                      data-testid={`report-lesson-${lesson.id}`}
                    >
                      <Flag className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    </button>
                    
                    {/* Color bar */}
                    <div className={`h-1 sm:h-1.5 bg-gradient-to-r ${categoryColors[category] || categoryColors.general}`} />
                    
                    {/* Image or Icon */}
                    {lesson.image_url || lesson.thumbnail_url ? (
                      <div className="relative aspect-[4/3] overflow-hidden">
                        <img 
                          src={lesson.image_url || lesson.thumbnail_url} 
                          alt={getLessonTitle(lesson)}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                        {/* Status indicators */}
                        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex gap-1">
                          {isCompleted && (
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-500 flex items-center justify-center">
                              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                            </div>
                          )}
                        </div>
                        {/* Category dot */}
                        <div className={`absolute top-1.5 left-1.5 sm:top-2 sm:left-2 w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-gradient-to-r ${categoryColors[category] || categoryColors.general}`}></div>
                      </div>
                    ) : (
                      <div className="aspect-[4/3] bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 flex items-center justify-center relative">
                        <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-gradient-to-r ${categoryColors[category] || categoryColors.general} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                          <BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                        </div>
                        {/* Status indicators */}
                        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 flex gap-1">
                          {isCompleted && (
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-green-500 flex items-center justify-center">
                              <CheckCircle className="w-3 h-3 sm:w-4 sm:h-4 text-white" />
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-3 sm:p-4 lg:p-5">
                      <h3 className="font-bold text-base sm:text-lg lg:text-xl text-slate-900 dark:text-slate-100 line-clamp-2 leading-snug mb-2 sm:mb-3">
                        {getLessonTitle(lesson)}
                      </h3>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 text-sm sm:text-base text-slate-500 dark:text-slate-400">
                          <Clock className="w-4 h-4 sm:w-5 sm:h-5" />
                          <span className="font-medium">{lesson.duration || 10} {getText('min', lang)}</span>
                        </div>
                        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-emerald-50 dark:bg-emerald-950/30 flex items-center justify-center group-hover:bg-emerald-100 dark:group-hover:bg-emerald-900/30 transition-colors">
                          <ArrowRight className={`w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400 group-hover:translate-x-0.5 transition-transform ${isRTL ? 'rotate-180' : ''}`} />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Bottom counter */}
            {lessons.length > 6 && (
              <div className="text-center mt-6 sm:mt-8 text-slate-600 dark:text-slate-400 text-sm sm:text-base font-medium">
                <p>{getText('total', lang)}: {lessons.length} {getText('lessons', lang)}</p>
              </div>
            )}
          </>
        )}
      </main>

      <div className="max-w-6xl mx-auto px-4 sm:px-6"><RelatedPages currentPage="lessons" /></div>
      <Footer />
      <ContentGateModal mode={showSubscribeModal || null} onClose={() => setShowSubscribeModal(false)} lang={lang} />

      {/* مودال الإبلاغ عن خطأ */}
      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-2xl p-6 relative" dir={isRTL ? 'rtl' : 'ltr'}>
            <button 
              onClick={() => setShowReportModal(false)}
              className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors`}
            >
              ✕
            </button>
            
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-950/30 rounded-full flex items-center justify-center">
                <Flag className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-slate-100">{getText('reportTitle', lang)}</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{getLessonTitle(reportingLesson)}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {getText('reportType', lang)}
                </label>
                <select
                  value={reportType}
                  onChange={(e) => setReportType(e.target.value)}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                >
                  <option value="incorrect_info">{getText('incorrectInfo', lang)}</option>
                  <option value="image_issue">{getText('imageIssue', lang)}</option>
                  <option value="translation_error">{getText('translationError', lang)}</option>
                  <option value="other">{getText('otherIssue', lang)}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {getText('details', lang)}
                </label>
                <textarea
                  value={reportDetails}
                  onChange={(e) => setReportDetails(e.target.value)}
                  placeholder={getText('describeIssue', lang)}
                  rows={4}
                  className="w-full p-3 border border-slate-300 dark:border-slate-600 rounded-xl focus:ring-2 focus:ring-orange-500 focus:border-orange-500 resize-none bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
                />
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="flex-1 py-3 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 font-semibold rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  {getText('cancel', lang)}
                </button>
                <button
                  onClick={handleSubmitReport}
                  disabled={!reportDetails.trim() || submittingReport}
                  className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-slate-300 dark:disabled:bg-slate-600 text-white font-semibold rounded-xl transition-colors"
                >
                  {submittingReport ? '...' : getText('submit', lang)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LessonsPage;
